import { useEffect } from 'react';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { useForm, FormProvider } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';

interface SubscriptionDetails {
  saasSubscriptionStatus?: string;
  purchaser?: {
    emailId?: string;
    tenantId?: string;
  };
  quantity?: number;
  planId?: string;
}

interface Organization {
  group_guid: string;
  group_name: string;
  subscription_status?: string;
  subscription_details?: SubscriptionDetails;
  number_users?: number;
  contact_email?: string;
  number_licenses?: number;
  utility_impact?: string;
}

interface SubscriptionLevel {
  label: string;
  value: string;
}

interface FormValues {
  group_name: string;
  subscription_level: string;
  quantity: number;
  utility_impact?: string;
}

interface EditOrganizationSubscriptionModalProps {
  show: boolean;
  onClose: () => void;
  orgData: Organization | null;
  onSave: (values: FormValues) => void;
  subscriptionLevels: SubscriptionLevel[];
  utilityImpactOptions?: { label: string; value: string }[];
}

const EditOrganizationSubscriptionModal: React.FC<
  EditOrganizationSubscriptionModalProps
> = ({
  show,
  onClose,
  orgData,
  onSave,
  subscriptionLevels,
  utilityImpactOptions,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      group_name: orgData?.group_name || '',
      subscription_level: orgData?.subscription_status || '',
      quantity: orgData?.number_licenses || 0,
      utility_impact: orgData?.utility_impact || 'None',
    },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (orgData) {
      reset({
        group_name: orgData.group_name || '',
        subscription_level: orgData.subscription_status || '',
        quantity: orgData.number_licenses || 0,
        utility_impact: orgData.utility_impact || 'None',
      });
    }
  }, [orgData, reset]);

  const onSubmit = (values: FormValues) => {
    onSave(values);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CustomModal
          show={show}
          heading="Edit Organization"
          onClose={onClose}
          onConfirm={handleSubmit(onSubmit)}
          saveButton="Save"
          buttonDisable={false}
        >
          <div className="row">
            <FormTextField
              title="Organization Name"
              fieldName="group_name"
              variant="full"
            />
            <FormSelectField
              title="Subscription Level"
              fieldName="subscription_level"
              options={subscriptionLevels}
              variant="full"
            />
            <FormTextField
              title="Number of Users Authorized"
              fieldName="quantity"
              variant="full"
            />
            <FormSelectField
              title="Utility Impact Prediction"
              fieldName="utility_impact"
              options={utilityImpactOptions}
              variant="full"
            />
          </div>
        </CustomModal>
      </form>
    </FormProvider>
  );
};

export default EditOrganizationSubscriptionModal;
